/**
 * Transforms a custom rule structure with per-condition scores into a valid RuleProperties array
 * compatible with json-rules-engine. Each condition becomes its own rule with the score in event.params.
 * @param customRule An object with a 'conditions.all' array, each with fact, value, operator, score
 * @param ruleName Optional base name for the rules
 */
export function transformCustomRuleToRuleProperties(
  customRule: { conditions: { all: Array<any> } },
  ruleName = "custom-rule"
): Array<RuleProperties> {
  if (
    !customRule?.conditions?.all ||
    !Array.isArray(customRule.conditions.all)
  ) {
    throw new Error(
      "Invalid custom rule structure: missing conditions.all array"
    );
  }
  // Use the combineRangeRules logic to preprocess
  const combined = combineRangeRules(customRule);
  return combined.map((rule: any, idx: number) => ({
    name: `${ruleName}-${idx}`,
    conditions: rule.all ? { all: rule.all } : { all: [rule] },
    event: {
      type: rule.fact || (rule.all && rule.all[0]?.fact) || `rule-${idx}`,
      params: { score: Number(rule.score) || 0 },
    },
  }));
}

// Helper to combine range rules for the same fact and score
function combineRangeRules(input: { conditions: { all: Array<any> } }) {
  // Group by fact for range detection
  const factGroups: Record<string, Array<any>> = {};
  for (const cond of input.conditions.all) {
    if (!factGroups[cond.fact]) factGroups[cond.fact] = [];
    factGroups[cond.fact].push(cond);
  }
  const combined: any[] = [];
  for (const [fact, group] of Object.entries(factGroups)) {
    // Check for a range (both greaterThanInclusive and lessThanInclusive)
    const gt = group.find((c) => c.operator === "greaterThanInclusive");
    const lt = group.find((c) => c.operator === "lessThanInclusive");
    if (gt && lt) {
      // Use only the score from the first rule in the group (usually gt)
      combined.push({
        all: [
          { fact: gt.fact, operator: gt.operator, value: gt.value },
          { fact: lt.fact, operator: lt.operator, value: lt.value },
        ],
        score: Number(gt.score) || 0,
      });
      continue; // Only use the first range per fact
    }
    // Otherwise, use only the first rule for this fact
    const first = group[0];
    if (first) {
      combined.push({
        fact: first.fact,
        operator: first.operator,
        value: first.value,
        score: Number(first.score) || 0,
      });
    }
  }
  return combined;
}
import { Engine, RuleProperties } from "json-rules-engine";

import {
  ApplicantData,
  ScoreResult,
  ScoreBreakdownItem,
  ScoreBand,
} from "./types";
import { Conditions } from "./conditions";
export { FACT_NAMES_BOOLEAN } from "./models/FactNamesBoolean";
export { FACT_NAMES_DATE } from "./models/FactNamesDate";
export { FACT_NAMES_STRING } from "./models/FactNamesString";
export { FACT_NAMES_NUMERIC } from "./models/FactNamesNumeric";
export {
  ApplicantData,
  ScoreResult,
  ScoreBreakdownItem,
  ScoreBand,
} from "./types";
export { Conditions } from "./conditions";

export type FactPreprocessor = (
  applicant: ApplicantData
) => Record<string, any>;

export class ScorecardEngine {
  private processedRulesList: Array<Array<any>>;
  private preprocessFacts: FactPreprocessor;
  private bandRules: RuleProperties[] = [];

  /**
   * Accepts a single rule set or an array of rule sets (passes).
   */
  constructor(
    rules:
      | { conditions: { all: Array<any> } }
      | Array<{ conditions: { all: Array<any> } }>,
    preprocessFacts?: FactPreprocessor,
    bands?: ScoreBand[]
  ) {
    // Support both single and multiple passes
    if (Array.isArray(rules)) {
      this.processedRulesList = rules.map((r) => combineRangeRules(r));
    } else {
      this.processedRulesList = [combineRangeRules(rules)];
    }
    this.preprocessFacts =
      preprocessFacts ||
      ((applicant: ApplicantData) => ({
        ...applicant,
      }));
    if (bands) {
      this.bandRules = this.scoreBandsToRules(bands);
    }
  }

  /**
   * Processes all passes in order, accumulating score and breakdown.
   */
  async score(
    applicant: ApplicantData
  ): Promise<ScoreResult & { band?: string; bandColor?: string }> {
    const facts = this.preprocessFacts(applicant);
    Object.keys(facts).forEach((key) => {
      if (facts[key] === undefined || facts[key] === null) {
        delete facts[key];
      }
    });

    let totalScore = 0;
    const breakdown: ScoreBreakdownItem[] = [];
    // For each pass, keep track of facts scored in that pass
    for (const processedRules of this.processedRulesList) {
      const scoredFacts = new Set<string>();
      for (const rule of processedRules) {
        const factsNeeded = rule.all
          ? rule.all.map((c: any) => c.fact)
          : [rule.fact];
        if (!factsNeeded.every((f: string) => f in facts)) {
          continue;
        }
        const fact = rule.fact || (rule.all && rule.all[0]?.fact) || "unknown";
        if (scoredFacts.has(fact)) {
          continue;
        }
        const engineRule = {
          conditions: rule.all ? { all: rule.all } : { all: [rule] },
          event: {
            type: fact,
            params: { score: Number(rule.score) || 0 },
          },
        };
        const engine = new Engine([engineRule]);
        const { events } = await engine.run(facts);
        if (events.length > 0) {
          totalScore += Number(rule.score) || 0;
          breakdown.push({
            category: fact,
            score: Number(rule.score) || 0,
            description: `Matched criteria for ${fact}, added ${rule.score} points`,
          });
          scoredFacts.add(fact);
        }
      }
    }

    // Band calculation
    let band: string | undefined;
    let bandColor: string | undefined;
    if (this.bandRules.length > 0) {
      const bandEngine = new Engine(this.bandRules);
      const bandResult = await bandEngine.run({ score: totalScore });
      const bandEvent = bandResult.events[0];
      if (bandEvent && bandEvent.params) {
        band = bandEvent.params.band;
        bandColor = bandEvent.params.color;
      }
    }
    return { totalScore, breakdown, band, bandColor };
  }

  private scoreBandsToRules(bands: ScoreBand[]): RuleProperties[] {
    // Find the band with the highest max value (numeric, not empty)
    const bandsWithMax = bands.filter((b) => b.scoreRange.max !== "");
    let topBand: ScoreBand | undefined = undefined;
    let topMax = -Infinity;
    for (const band of bandsWithMax) {
      const max = Number(band.scoreRange.max);
      if (!isNaN(max) && max > topMax) {
        topMax = max;
        topBand = band;
      }
    }

    return bands
      .filter((b) => b.scoreRange.min !== "" && b.scoreRange.max !== "")
      .map((band) => {
        // If this is the top tier band, only check min (score >= min)
        if (topBand && band.name === topBand.name) {
          return {
            name: band.name,
            conditions: {
              all: [
                {
                  fact: "score",
                  operator: "greaterThanInclusive",
                  value: Number(band.scoreRange.min),
                },
              ],
            },
            event: {
              type: "set-band",
              params: { band: band.name, color: band.color },
            },
          };
        } else {
          return {
            name: band.name,
            conditions: {
              all: [
                {
                  fact: "score",
                  operator: "greaterThanInclusive",
                  value: Number(band.scoreRange.min),
                },
                {
                  fact: "score",
                  operator: "lessThanInclusive",
                  value: Number(band.scoreRange.max),
                },
              ],
            },
            event: {
              type: "set-band",
              params: { band: band.name, color: band.color },
            },
          };
        }
      });
  }
}
